import { Link } from "react-router-dom";
import Button from "@/components/base/Button";
import type { ServiceArea } from "@/data/serviceAreas";
import { clinicInfo } from "@/mocks/site";

interface MajorServiceSectionProps {
  area: ServiceArea;
  index: number;
}

const toneStyles = {
  spine: {
    eyebrow: "text-secondary-700",
    line: "bg-secondary-400",
    bubble: "bg-secondary-100 text-secondary-700",
    check: "bg-secondary-100 text-secondary-700",
    wash: "from-secondary-100 to-accent-100",
    statCard: "bg-secondary-100/70",
    statText: "text-secondary-700",
    glow: "bg-secondary-100/60",
  },
  ortho: {
    eyebrow: "text-accent-700",
    line: "bg-accent-400",
    bubble: "bg-accent-100 text-accent-700",
    check: "bg-accent-100 text-accent-700",
    wash: "from-accent-100 to-primary-100",
    statCard: "bg-accent-100/70",
    statText: "text-accent-700",
    glow: "bg-accent-100/60",
  },
};

/**
 * Large editorial feature block for a primary service area (Spine Care or
 * Orthopaedic Care). Image and content alternate sides down the page for an
 * editorial rhythm rather than a plain two-column card.
 */
export default function MajorServiceSection({ area, index }: MajorServiceSectionProps) {
  const reverse = index % 2 === 1;
  const tone = toneStyles[area.tone];

  return (
    <section
      id={area.slug}
      className={`scroll-mt-28 py-16 md:py-24 ${reverse ? "bg-background-50" : "bg-background-100"}`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <div className={`reveal relative lg:col-span-6 ${reverse ? "lg:order-2" : "lg:order-1"}`}>
            <div
              className={`absolute -left-5 -top-5 bottom-16 w-2/3 rounded-2xl bg-gradient-to-br ${tone.wash}`}
              aria-hidden="true"
            ></div>
            <div className="relative overflow-hidden rounded-2xl border border-background-200 bg-background-100">
              <div className="h-[360px] w-full md:h-[480px]">
                <img
                  src={area.image}
                  alt={area.imageAlt}
                  title={`${area.eyebrow} at Ortho Spine Care, Trichy`}
                  className="h-full w-full object-cover object-top transition-transform duration-700 ease-out hover:scale-105"
                />
              </div>
            </div>
            <div className="absolute -bottom-6 right-4 flex items-center gap-3 rounded-2xl border border-background-200 bg-background-50/95 px-5 py-4 shadow-[0_20px_45px_-30px_rgba(15,40,80,0.5)] backdrop-blur md:right-8">
              <span className={`flex h-11 w-11 flex-none items-center justify-center rounded-md ${tone.bubble}`}>
                <i
                  className={area.tone === "spine" ? "ri-body-scan-line text-xl" : "ri-focus-3-line text-xl"}
                  aria-hidden="true"
                ></i>
              </span>
              <span>
                <span className="block font-heading text-sm font-semibold text-foreground-950">{area.title}</span>
                <span className="block font-label text-xs text-foreground-600">{area.intro}</span>
              </span>
            </div>
          </div>

          <div className={`reveal lg:col-span-6 ${reverse ? "lg:order-1" : "lg:order-2"}`}>
            <span
              className={`inline-flex items-center gap-2 font-label text-xs font-semibold uppercase tracking-[0.18em] ${tone.eyebrow}`}
            >
              <span className={`h-px w-8 ${tone.line}`} aria-hidden="true"></span>
              {area.eyebrow}
            </span>
            <h2 className="mt-5 font-heading text-3xl font-semibold leading-[1.14] text-foreground-950 md:text-4xl">
              {area.title}
            </h2>
            <p className="mt-5 font-body text-sm leading-relaxed text-foreground-600 md:text-base">
              {area.description}
            </p>

            <ul className="mt-7 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
              {area.highlights.map((item) => (
                <li key={item} className="flex items-start gap-3 font-body text-sm text-foreground-700">
                  <span className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full ${tone.check}`}>
                    <i className="ri-check-line text-xs" aria-hidden="true"></i>
                  </span>
                  {item}
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              {area.stats.map((stat) => (
                <div key={stat.label} className={`rounded-xl px-4 py-3 ${tone.statCard}`}>
                  <span className={`block font-heading text-base font-semibold ${tone.statText}`}>{stat.value}</span>
                  <span className="mt-0.5 block font-label text-xs text-foreground-600">{stat.label}</span>
                </div>
              ))}
            </div>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Button to={area.href} size="lg" icon="ri-arrow-right-line">
                {area.ctaLabel}
              </Button>
              <Button href={clinicInfo.phoneHref} variant="outline" size="lg" icon="ri-phone-fill">
                Call {clinicInfo.phoneDisplay}
              </Button>
            </div>

            <Link
              to="/appointment"
              className="mt-5 inline-flex items-center gap-2 font-label text-sm font-semibold text-primary-700 hover:text-primary-800"
            >
              <i className="ri-calendar-check-line" aria-hidden="true"></i>
              Book an appointment for {area.eyebrow}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}